import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import moment from 'moment';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { Budget, BudgetRecord, EmpGroup, GroupTypeRecord } from '../../models/budget.model';
import { BudgetService } from '../../services/budget/budget.service';
import { Subscription } from 'rxjs';
import { MenuItem } from '@niceltd/sol/floating-menu/src/lib/menu-items';
import { columnList, headerItems } from './export-budget-meter';
import { TypeFormats } from 'src/shared/types/exportItem';

@Component({
  selector: 'cv-admin-budget-meter',
  templateUrl: './cv-admin-budget-meter.component.html',
  styleUrl: './cv-admin-budget-meter.component.scss'
})
export class CvAdminBudgetMeterComponent implements OnInit, OnDestroy {
  @Output() loadingStateChange = new EventEmitter<boolean>();
  private budgetSubscription: Subscription;

  budget: Budget = {
    budgetList: [],
    total: "",
    initBudgetList: []
  };

  isLoading: boolean = false;
  displayBudgetDetails: boolean = false;
  fileName: string = "";
  isTeam: boolean = false;

  defaultMenuOptions: MenuItem[] = [
    {
      label: "Excel",
      svgIconName: "icon-office-excel",
      callbackFn: this.onExportClick.bind(this),
    },
    {
      label: "CSV",
      svgIconName: "icon-CSV",
      callbackFn: this.onExportClick.bind(this),
    }
  ];
  
  private readonly monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  get sectionTitle(): string {
    return this.getLocalizationString("Budget_Meter", "Budget Meter");
  }

  get budgetMeterSectionTitle(): string {
    const currentMonthIndex = moment().month();
    const monthName = this.monthNames[currentMonthIndex];
    const localizedMonth = this.getLocalizationString(monthName, monthName);

    return this.cvLocalizationService.Format(
      this.getLocalizationString("Budget_Meter_Section_Title", "{0} Budget Overview"),
      localizedMonth
    );
  }

  get totalBudget(): number {
    return this.budget?.budgetList?.reduce((sum, budget) => sum + budget.Amount, 0) || 0;
  }

  get totalCoinsAwarded(): number {
    return this.budget?.budgetList?.reduce((sum, budget) => sum + budget.CoinsAwarded, 0) || 0;
  }

  get coinsAvailable(): number {
    return (this.totalBudget - this.totalCoinsAwarded) > 0 ? this.totalBudget - this.totalCoinsAwarded : 0
  }

  get groupLevelName(): 'Team' | 'Company' {
    return this.isTeam ? 'Team' : 'Company';
  }

  set isLoadingState(value: boolean) {
    this.loadingStateChange.emit(value);
  }

  constructor(
    private cvLocalizationService: CVLocalizationService,
    private budgetService: BudgetService,
  ) {}

  ngOnInit() {
    // Subscribe to budget updates
    this.budgetSubscription = this.budgetService.budgetList$.subscribe(
      (budgetList) => {
        if (budgetList.length > 0) {
          this.budget.budgetList = budgetList.map((budget) => ({
            ...budget,
            AmountString: budget.Amount.toString(),
          })).sort((a, b) => a.GroupLevelName.localeCompare(b.GroupLevelName, undefined, { sensitivity: 'base' }));
          this.budget.initBudgetList = [...this.budget.budgetList];

          // Add validation before accessing the GroupLevel property
          this.isTeam = this.budget.budgetList[0].GroupLevel === 2;
        }
      }
    );
    // Initial data load
    this.initialize();
  }

  async onExportClick(item: MenuItem) {
    const formatType: TypeFormats = item.label === "Excel" ? "excel" : "csv";
    const body = {
      columnList: columnList(this.groupLevelName),
      headerItems: headerItems(this.totalBudget, this.totalCoinsAwarded),
      values: this.getValues(),
      format: formatType,
      fileNameExport: {
        "name": "Budget_Meter",
        "section": "Budget"
      },
      fileName: this.fileName,
      notification: {
        "section": "Budget",
        "field": "Budget_Meter",
        "defaultMessage": "Budget Meter"
      },
    }

    await this.budgetService.exportBudgetDefault(formatType, body, this.fileName);
    
  }

  getValues() {
    return this.budget.budgetList.map((budget) => ({
      [`Budget_${this.groupLevelName}`]: budget.GroupLevelName,
      [`Monthly_${this.groupLevelName}_Budget`]: budget.AmountString,
      [`${this.groupLevelName}_Coins_Available`]: this.availableBudget(budget.Amount - budget.CoinsAwarded),
      [`${this.groupLevelName}_Total_Coins_Awarded`]: budget.CoinsAwarded.toString(),
      [`${this.groupLevelName}_Coins_Percentage_Available`]: this.getBudgetPercentage(budget.Amount, budget.Amount - budget.CoinsAwarded),
    }));
  }

  ngOnDestroy() {
    // Clean up subscription
    if (this.budgetSubscription) {
      this.budgetSubscription.unsubscribe();
    }
  }

  async initialize() {
    try {
      this.isLoading = true;
      this.isLoadingState = true;
      await this.loadLocalization();
      this.fileName = await this.cvLocalizationService.GetLocalText("Budget", "Budget_Meter", "Budget Meter");
    } finally {
      this.isLoading = false;
      this.isLoadingState = false;
    }
  }

  async loadLocalization() {
    await this.cvLocalizationService.GetSection("Budget_Meter");
  }

  getLocalizationString(key: string, defaultValue: string) {
    return this.cvLocalizationService.getCachedText("Budget_Meter", key, defaultValue);
  }

  getRemainingCoins(total: number, awarded: number): number {
    if (isNaN(+total) || +total <= 0 || isNaN(+awarded)) return 0;
    return total - awarded;
  }

  getBudgetPercentage(total: number, awarded: number): string {
    if (isNaN(+total) || +total <= 0 || isNaN(+awarded)) return '';
    const percentage = (Math.max(Math.min(awarded / total * 100, 100), 0));
    return `${percentage.toFixed(2)}%`;
  }

  getProgressClass(awardedPercentage: number): string {
    if (awardedPercentage < 50) {
      return "bg-objectiveGreen";
    } else if (awardedPercentage >= 50 && awardedPercentage < 75) {
      return "bg-objectiveYellow";
    } else {
      return "bg-objectiveRed";
    }
  }

  toggleDisplayBudgetDetails(): void {
    this.displayBudgetDetails = !this.displayBudgetDetails;
  }

  availableBudget(value: number): number {
    let availableBudget = value;
    if (availableBudget <= 0) {
      return 0;
    }

    return availableBudget;
  }

  formatValue(value: number): string {
    if (isNaN(value) || value === null || value === undefined) return '0';
    return value.toLocaleString(CVCultureName);
  }
}
